import { useEffect } from "react";
import { formatArs } from "../../../shared/money";
import { freeShippingEnabled, freeShippingMin, freeShippingZones, shippingRates, zonesText, type RatedZone } from "../../../shared/shipping";
import { Link } from "react-router";
import { useI18n } from "../../i18n/I18nProvider";
import { useCatalog } from "../../lib/CatalogProvider";
import type { InfoPageDef } from "./pages";
import { DEFAULT_CONTACT_EMAIL, LEGAL_UPDATED } from "./types";

const EMAIL_MARK = "\u0000";
/** Email placeholder, internal links [[/path|label]] and URLs (a trailing period or comma stays in the text). */
const TOKENS = /(\u0000|\[\[\/[^|\]]*\|[^\]]+\]\]|https:\/\/\S+?(?=[.,]?(?:\s|$)))/;
const INTERNAL = /^\[\[(\/[^|\]]*)\|([^\]]+)\]\]$/;

/** Plain text with links (the texts are ours, never user input). */
function Rich({ text, email, whatsapp }: { text: string; email: string; whatsapp: string }) {
  const parts = text.replaceAll("{email}", EMAIL_MARK).replaceAll("{whatsapp}", whatsapp).split(TOKENS);
  return (
    <>
      {parts.map((part, i) => {
        if (part === EMAIL_MARK) return <a key={i} href={`mailto:${email}`}>{email}</a>;
        const internal = INTERNAL.exec(part);
        if (internal) {
          return (
            <Link key={i} to={internal[1]}>
              {internal[2]}
            </Link>
          );
        }
        if (part.startsWith("https://")) {
          return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer">
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
}

/** Legal texts, guides and help: public even while the store is "Próximamente". */
export function InfoPage({ page }: { page: InfoPageDef }) {
  const { locale, t, pick } = useI18n();
  const { settings } = useCatalog();
  const text = page.doc[locale];
  const email = (settings.contact_email && pick(settings.contact_email)) || DEFAULT_CONTACT_EMAIL;
  const whatsapp = (settings.whatsapp && pick(settings.whatsapp)) || "";
  const freeOn = freeShippingEnabled(settings.free_shipping_enabled ? pick(settings.free_shipping_enabled) : null);
  const freeMin = formatArs(freeShippingMin(settings.free_shipping_min_ars ? pick(settings.free_shipping_min_ars) : null), locale);
  const freeZones = zonesText(freeShippingZones(settings.free_shipping_zones ? pick(settings.free_shipping_zones) : null), locale);
  const rates = shippingRates(settings.shipping_rates_ars ? pick(settings.shipping_rates_ars) : null);
  const show = (p: string) =>
    (whatsapp || !p.includes("{whatsapp}")) &&
    !(p.startsWith("{if:free}") && !freeOn) &&
    !(p.startsWith("{if:nofree}") && freeOn) &&
    !(p.startsWith("{if:other}") && !rates.other) &&
    !(p.startsWith("{if:noother}") && rates.other);
  const fill = (p: string) =>
    p
      .replace(/^\{if:(no)?(free|other)\}/, "")
      .replaceAll("{freeMin}", freeMin)
      .replaceAll("{freeZones}", freeZones)
      .replace(/\{rate:(caba|cordon1|cordon2|cordon3)\}/g, (_, z: RatedZone) => formatArs(rates[z], locale))
      .replaceAll("{rate:other}", rates.other ? formatArs(rates.other, locale) : "");

  useEffect(() => {
    document.title = `${text.title} · pokefans.fun`;
  }, [text.title]);

  return (
    <article className="container section legal">
      <h1>{text.title}</h1>
      {page.dated && (
        <p className="muted">{t("legal.updated", { date: new Date(`${LEGAL_UPDATED}T12:00:00`).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { dateStyle: "long" }) })}</p>
      )}
      <p>
        <Rich text={text.intro} email={email} whatsapp={whatsapp} />
      </p>
      {text.sections.filter((s) => s.paragraphs.some(show)).map((s) => (
        <section key={s.title}>
          <h2>{s.title}</h2>
          {s.paragraphs.filter(show).map((p, i) => (
            <p key={i}>
              <Rich text={fill(p)} email={email} whatsapp={whatsapp} />
            </p>
          ))}
        </section>
      ))}
    </article>
  );
}
